import {useState, useEffect} from 'react';
import { debounce} from 'lodash';
import { useErrorBoundary } from 'react-error-boundary';
import { Divider, Grid2 as Grid, Typography, Alert, Stack, FormControl, OutlinedInput, InputAdornment, InputLabel, Select, MenuItem, CircularProgress, Box, useMediaQuery, useTheme, styled} from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import {Search} from '@mui/icons-material';

import { IepacYearEndReportColumns} from '../../../types/referenceData/reportTableColumns';
import PeriodicReport from '../../../types/reports/periodicReportContent';
import { DataGrid } from '@mui/x-data-grid';
import ReportListExportSession from './ReportListExportSection';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const StyledDataGrid = styled(DataGrid)(() => ({
  '& .even-row': {
    backgroundColor: '#f9f9f9',
  },
  '& .odd-row': {
    backgroundColor: '#ffffff',
  },
}));


const IepacYearEndReports = (): JSX.Element => {
    
    document.title = 'OCPF - Local Party Committee Reports';

    const { showBoundary } = useErrorBoundary();  
    const dropDownYears = Array.from(new Array(12), (val, index) => new Date().getFullYear() - index);

    const theme = useTheme(); 
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(true);
    const [tableColumns, setColumns] = useState(IepacYearEndReportColumns);
    const [tableRows, setRows] = useState<PeriodicReport[]>();
    const [reports, setReports] = useState<PeriodicReport[]>();
    const [reportsSummary, setReportsSummary] = useState('No reports found.');
    const [reportYear, setReportYear] = useState(new Date().getFullYear()-1);
    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');

    useEffect(() => {
        getIepacYearEndReportList();
      }, [reportYear])

    const getIepacYearEndReportList = async () => {

        setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/lpc/excelOutput/${reportYear}`);
        setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/lpc/textOutput/${reportYear}`);

        try{
          //revisit later
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/iepac/${reportYear}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the IEPAC Year End reports.'));
            }
            return result.json();
          })
          .then(result => {
            let count = result.length;
            if (count === 0){
                setReportsSummary("No reports found.");
                setRows([]);
            }
            else{
                setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
                setRows(result);
                setReports(result);
            }
            setIsLoading(false);
          });
        }catch(error){
            console.error('Uncaught Error:', error);
            showBoundary(new Error(`${error}`));
        }
      }
      
      const updateReporYear = (event: any) => {
        setReportYear(event.target.value);
      };

     
      const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if(searchInput.length === 0){
          setRows(reports);
        }
        else{
          let filteredRows = reports?.filter((row) => row.filerName.toLowerCase().includes(searchInput)) || [];
          let count = filteredRows.length;
          setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
          setRows(filteredRows);
        }
      }, 800);

    return (
      <Main>
        <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            gutterBottom
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            marginBottom={2}
            >
            Independent Expenditure PAC Year-End Reports
        </Typography>
        <Divider/>
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack direction={"row"} spacing={1} 
                sx={{ mb: 1}}
                >
                 <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="year-id">Year</InputLabel>
                    <Select 
                        labelId="year-id"
                        id="year-select"
                        aria-labelledby='year-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Report Year"
                        value={reportYear}
                        onChange={updateReporYear}
                    >
                    {
                        dropDownYears.map((year) =>
                            <MenuItem value={year} key={year} >{year}</MenuItem>
                        )
                    }
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 200 }}>
                        <OutlinedInput
                            aria-label='filter by IEPAC name'
                            placeholder={"Filter by IEPAC name"}
                            startAdornment={
                                <InputAdornment position="start">
                                <Search />
                                </InputAdornment>
                            }
                            onChange={filterByName}
                            sx={{ fontSize: 'smaller' }}
                        />

                </FormControl>
            </Stack>
        </Grid>
        <Grid size={11.5}>
            <Alert icon={false} severity="info"
            sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                '.MuiAlert-message': { 
                padding: '4px',
                } 
            }}
        >
            {reportsSummary}
            </Alert>
        </Grid>
      </Grid>
       {/*report table*/}
       <Box 
        sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: '95%',
        '.MuiDataGrid-columnHeaderTitle': { 
            fontWeight: 'bold !important',
        },
        }}>
        <StyledDataGrid
            rows={tableRows}
            getRowHeight={isSM? ()=> 'auto': () => 60}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
              ? 'even-row'
              : 'odd-row'
            }
            columns={tableColumns}
            getRowId={(row) => row.cpfId}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            sx={{ 
              '.MuiDataGrid-cell': {
                fontSize: isSM ? '0.875rem' : '1rem',
                whiteSpace: 'normal', 
                wordBreak: 'break-word',
                //paddingTop: '2px!important',
                //paddingBottom: '2px!important',
              },
            }}
        />
      </Box>
       {/*loading indicator*/}
       <CircularProgress
            aria-label='loading'
            size={50}
            className={isLoading? "": "hidden"}
            sx={{
              position: "fixed",
              left: "50%",
              top: "20%",
              transform: "translate(-50%, -50%)",
              zIndex: 2
             }}
        />
    </Container>
    </Main>
  );
};

export default IepacYearEndReports;
